/**
 * Entrada de sessao desta aplicacao.
 *
 * Ela nao tem tela de login: quem autentica e o admin. O usuario clica
 * no item do menu de la e chega aqui com um handoff no *fragmento* da URL,
 * nunca na query string -- fragmento nao viaja no log do servidor nem no
 * cabecalho Referer, e some da barra de enderecos no primeiro replaceState.
 *
 * Dois formatos, nesta ordem de preferencia:
 *
 *   1. **ticket** (`/sso#k=<ticket>`) -- o atual. Opaco, de uso unico e vida
 *      curta; sozinho nao abre nada. Ele e trocado por um token proprio desta
 *      aplicacao em `POST /sso/exchange`, e esse token ja vem com `aud` do
 *      accounts e com o `redirect_to` que o admin pediu.
 *   2. **token** (`/sso#t=<jwt>&c=<corp>`) -- o formato anterior, aceito
 *      enquanto houver admin apontando para uma API de auth sem emissao de
 *      ticket. Traz o token de sessao inteiro na URL, que e o que o ticket veio
 *      resolver.
 *
 * O motivo de existir este arquivo: localStorage e por origem, e
 * admindev.hliberty.net e lpbuilder.admindev.hliberty.net sao origens diferentes.
 * A sessao do admin nao atravessa sozinha. Esta e a unica ponte entre as duas.
 */

const CHAVE_TICKET = "k";
const CHAVE_TOKEN = "t";
const CHAVE_CORP = "c";
const CHAVE_DESTINO = "r";

/** Como o pedido de volta viaja ate o admin, na query da URL de login. */
const CHAVE_APP = "app";
const CHAVE_RETORNO = "r";
const CHAVE_MOTIVO = "motivo";

/**
 * Quantas idas seguidas ao admin sem sessao ate parar de pedir a volta.
 *
 * Existe para cortar pingue-pongue: se o handoff falhar de forma persistente
 * -- ticket recusado, relogio fora de hora, permissao removida no meio --, o
 * admin manda de volta para ca, aqui nao ha sessao de novo, e os dois ficariam
 * se devolvendo o usuario para sempre. Passado o limite, a volta e abandonada
 * e a pessoa fica no admin, que e onde da para resolver.
 */
const LIMITE_DE_TENTATIVAS = 2;
const CHAVE_TENTATIVAS = "sso:tentativas-de-retorno";

/** Para onde o usuario vai quando o handoff da certo e nao pediu destino. */
// A primeira tela funcional é a lista de projetos do LPBuilder.
export const DESTINO_PADRAO = "/projetos";

/**
 * Le o handoff do fragmento sem consumi-lo. Devolve null quando nao ha handoff
 * na URL -- o caso normal de quem recarrega a pagina depois de ja ter entrado.
 *
 * `tipo` diz qual dos dois formatos chegou; quem trata e a SsoPage.
 */
export function lerHandoff() {
    const fragmento = window.location.hash.replace(/^#/, "");

    if (!fragmento) {
        return null;
    }

    const parametros = new URLSearchParams(fragmento);
    const ticket = parametros.get(CHAVE_TICKET);

    if (ticket) {
        // O destino nao vem na URL: quem o guarda e o servidor, junto do
        // ticket, e ele volta no `redirect_to` da troca.
        return { tipo: "ticket", ticket };
    }

    const token = parametros.get(CHAVE_TOKEN);

    if (!token) {
        return null;
    }

    return {
        tipo: "token",
        token,
        // A corporacao ativa precisa vir junto: ela mora na sessao do servidor
        // de auth (`Session::put('corp_id')`), nao nas claims do JWT, e o
        // cookie dessa sessao nao e confiavel entre sites. Nao e segredo -- e
        // um id que o usuario ja possui, e a API recusa se ele nao pertencer a
        // corporacao. Sem ela, a lista de usuarios cai no /user global em vez
        // do /corp/{id}/users.
        corp: parametros.get(CHAVE_CORP) || null,
        // So caminho interno: destino absoluto vindo da URL seria open redirect.
        destino:
            caminhoInterno(parametros.get(CHAVE_DESTINO)) ?? DESTINO_PADRAO,
    };
}

/**
 * Tira o token da barra de enderecos assim que ele foi guardado. Sem isto o
 * JWT fica no historico do navegador e em qualquer print de tela.
 */
export function limparFragmento() {
    window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
    );
}

/** URL do admin, de onde a sessao veio e para onde ela volta. */
export function urlDoAdmin(caminho = "/home") {
    const base = (window.appConfig?.URL_APP_ADMIN || "").replace(/\/+$/, "");

    if (!base) {
        return null;
    }

    return `${base}${caminho.startsWith("/") ? caminho : `/${caminho}`}`;
}

/**
 * Devolve o usuario ao admin. E o que o accounts faz no lugar de mostrar uma
 * tela de login: sem sessao aqui, quem resolve isso e o admin.
 *
 * `retornarPara` e o caminho que a pessoa estava tentando abrir. Quando ele
 * vem, a URL do admin leva junto qual aplicacao pediu e para onde voltar --
 * e, terminado o login, o admin emite um ticket para ca e traz a pessoa de
 * volta ao lugar de onde ela saiu, em vez de largar ela na home do admin.
 *
 * O que viaja e so o **caminho**: a origem de destino nao vem daqui, vem do
 * registry no servidor, na hora de emitir o ticket. Mandar a URL inteira seria
 * dar a quem monta o link o poder de escolher o destino do redirecionamento.
 */
export function voltarParaOAdmin({ caminho = "/", motivo, retornarPara } = {}) {
    const base = urlDoAdmin(caminho);

    if (!base) {
        // Sem URL_APP_ADMIN configurada nao ha para onde mandar. Falhar visivel
        // e melhor do que um laco de redirecionamento para lugar nenhum.
        console.error(
            "URL_APP_ADMIN nao configurada: nao ha admin para onde voltar.",
        );
        return false;
    }

    const querVoltar = retornarPara !== undefined && podeTentarRetorno();

    if (querVoltar) {
        registrarTentativaDeRetorno();
    }

    window.location.replace(
        montarUrlDeRetorno({
            base,
            slug: querVoltar ? slugDaAplicacao(window.appConfig) : null,
            destino: querVoltar ? retornarPara : null,
            motivo,
        }),
    );

    return true;
}

/**
 * Monta a URL do admin com o pedido de volta. Pura, para dar para testar.
 *
 * Sem slug nao ha pedido de volta nenhum -- o admin nao teria a quem emitir o
 * ticket. Com slug e sem caminho ainda vale: a pessoa volta para a pagina
 * inicial da aplicacao, que e melhor do que ficar na home do admin.
 */
export function montarUrlDeRetorno({ base, slug, destino, motivo } = {}) {
    if (!base) {
        return null;
    }

    const parametros = new URLSearchParams();

    if (motivo) {
        parametros.set(CHAVE_MOTIVO, motivo);
    }

    if (slug) {
        parametros.set(CHAVE_APP, slug);

        const interno = caminhoInterno(destino);
        if (interno) {
            parametros.set(CHAVE_RETORNO, interno);
        }
    }

    const query = parametros.toString();

    return query ? `${base}?${query}` : base;
}

/**
 * Como esta aplicacao se identifica no registry.
 *
 * O mesmo `app.slug` cadastrado no admin -- e o que o admin usa para pedir o
 * ticket de volta para ca. Vem do APP_NAME porque e o que o /config ja expoe:
 * `hLiberty.accounts` vira `hliberty.accounts`.
 */
export function slugDaAplicacao(config = {}) {
    const slug = config?.APP_SLUG || config?.APP_NAME || "";

    return String(slug).trim().toLowerCase() || null;
}

function podeTentarRetorno() {
    try {
        return (
            Number(window.sessionStorage.getItem(CHAVE_TENTATIVAS) || 0) <
            LIMITE_DE_TENTATIVAS
        );
    } catch {
        // Aba sem sessionStorage (modo restrito): sem contador, mas o admin so
        // manda de volta uma vez por pedido, entao nao ha laco infinito.
        return true;
    }
}

function registrarTentativaDeRetorno() {
    try {
        const atual = Number(
            window.sessionStorage.getItem(CHAVE_TENTATIVAS) || 0,
        );
        window.sessionStorage.setItem(CHAVE_TENTATIVAS, String(atual + 1));
    } catch {
        // Ver podeTentarRetorno.
    }
}

/** Entrou: o contador de pingue-pongue zera. */
export function limparTentativasDeRetorno() {
    try {
        window.sessionStorage.removeItem(CHAVE_TENTATIVAS);
    } catch {
        // Ver podeTentarRetorno.
    }
}

function caminhoInterno(valor) {
    // "/users" serve; "//evil.com" e "https://evil.com" nao.
    if (!valor || !valor.startsWith("/") || valor.startsWith("//")) {
        return null;
    }

    return valor;
}

/**
 * Se a resposta diz que a sessao foi encerrada em outro lugar.
 *
 * O logout no admin (ou aqui) derruba a sessao inteira -- todos os tokens
 * nascidos daquela entrada, em todas as aplicacoes. Sem reconhecer isso, esta
 * aba continuaria aberta mostrando erro generico ate alguem recarregar.
 *
 * Estreito de proposito: 401 generico continua tratado como antes, senao token
 * vencido viraria logout em vez de renovacao.
 */
export function sessaoFoiRevogada(erro) {
    const status = erro?.status ?? erro?.response?.status;

    return status === 401 && erro?.response?.data?.error === "Session revoked.";
}

/**
 * O token local nasce na propria LPBuilder API. Se a chave JWT local for
 * trocada durante o desenvolvimento, o token guardado no navegador deixa de
 * ser verificavel. Nesse caso, a unica recuperacao segura e entrar de novo.
 */
export function sessaoLocalEstaInvalida(erro) {
    const status = erro?.status ?? erro?.response?.status;
    const mensagem = erro?.response?.data?.message;

    return status === 401 && mensagem === "Assinatura invalida.";
}
